/**
 * Product Variant Picker - Storefront API Integration
 * Handles color swatches for products with 2048+ variants (ModeSplit)
 * Custom development for CheapTshirtUSA.com
 * All right reserved by CheapTshirtUSA © 2025.
 * Developed by Senior Shopify Designer & Developer Arafat Jahan https://arafatjahan.com
 * Version 1.1 revised 0.4
 */
;(() => {
  // Read config from Liquid
  var config = window.variantPickerConfig || {}
  var STOREFRONT_API_TOKEN = config.apiToken || ""
  var shopDomain = config.shopDomain || ""
  var productId = config.productId || ""

  var colorSwatchMap = {}
  var colorCodeMap = {}
  var variantDataMap = {}
  
  // Cache settings (1 hour cache for variant color/swatch data)
  var CACHE_PREFIX = "cc_variant_picker_"
  var CACHE_DURATION = 60 * 60 * 1000 // 1 hour in milliseconds
  
  // Cache helpers
  function getCachedData(key) {
    try {
      var cached = localStorage.getItem(CACHE_PREFIX + key)
      if (cached) {
        var parsed = JSON.parse(cached)
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          return parsed.data
        }
        localStorage.removeItem(CACHE_PREFIX + key)
      }
    } catch (e) {}
    return null
  }
  
  function setCachedData(key, data) {
    try {
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
        data: data,
        timestamp: Date.now()
      }))
    } catch (e) {
      cleanOldCache()
    }
  }
  
  function cleanOldCache() {
    try {
      var keysToRemove = []
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i)
        if (key && key.startsWith(CACHE_PREFIX)) {
          try {
            var item = JSON.parse(localStorage.getItem(key))
            if (Date.now() - item.timestamp > CACHE_DURATION) {
              keysToRemove.push(key)
            }
          } catch (e) {
            keysToRemove.push(key)
          }
        }
      }
      keysToRemove.forEach(function(k) { localStorage.removeItem(k) })
    } catch (e) {}
  }
  
  // Get current language from HTML lang attribute
  var currentLang = document.documentElement.lang || 'en'
  var shopifyLangCode = currentLang.split('-')[0].toUpperCase()
  
  // Cache key includes language to avoid returning wrong language data
  var cacheKey = productId + '_' + shopifyLangCode

  async function fetchAllVariants() {
    if (!STOREFRONT_API_TOKEN) {
      return
    }
    
    // Check cache first (1 hour cache)
    var cachedVariants = getCachedData(cacheKey)
    if (cachedVariants) {
      processVariants(cachedVariants)
      return
    }

    var allVariants = []
    var hasNextPage = true
    var cursor = null

    while (hasNextPage) {
      var query = `
        query getProductVariants($id: ID!, $first: Int!, $after: String, $language: LanguageCode) @inContext(language: $language) {
          product(id: $id) {
            id
            title
            variants(first: $first, after: $after) {
              pageInfo {
                hasNextPage
                endCursor
              }
              edges {
                node {
                  id
                  title
                  sku
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  image {
                    url
                    altText
                  }
                  metafields(identifiers: [
                    {namespace: "custom", key: "colorswatch"},
                    {namespace: "custom", key: "color_code"}
                  ]) {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      `

      var variables = {
        id: "gid://shopify/Product/" + productId,
        first: 250,
        after: cursor,
        language: shopifyLangCode,
      }

      try {
        var response = await fetch("https://" + shopDomain + "/api/2024-01/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": STOREFRONT_API_TOKEN,
          },
          body: JSON.stringify({ query: query, variables: variables }),
        })

        if (!response.ok) {
          return
        }

        var data = await response.json()

        if (data.errors) {
          return
        }

        if (!data.data || !data.data.product) {
          return
        }

        if (!data.data.product.variants) {
          return
        }

        var variants = data.data.product.variants
        allVariants = allVariants.concat(variants.edges.map((e) => e.node))

        hasNextPage = variants.pageInfo.hasNextPage
        cursor = variants.pageInfo.endCursor
      } catch (error) {
        return
      }
    }
    
    // Save to cache (1 hour)
    setCachedData(cacheKey, allVariants)
    
    // Process the fetched variants
    processVariants(allVariants)
  }
  
  function processVariants(allVariants) {
    allVariants.forEach((variant) => {
      var colorOption = variant.selectedOptions.find((opt) =>
        ["color", "colour", "couleur", "cor", "colore", "farbe"].includes(opt.name.toLowerCase()),
      )

      if (colorOption) {
        var colorValue = colorOption.value
        var numericId = variant.id.replace("gid://shopify/ProductVariant/", "")

        if (!variantDataMap[colorValue]) {
          variantDataMap[colorValue] = []
        }

        variantDataMap[colorValue].push({
          id: numericId,
          sku: variant.sku || "",
          availableForSale: variant.availableForSale,
          image: variant.image ? variant.image.url : null,
          imageAlt: variant.image ? variant.image.altText : null,
          title: variant.title,
        })

        if (variant.metafields) {
          variant.metafields.forEach((mf) => {
            if (mf && mf.key === "colorswatch" && mf.value) {
              var swatchUrl = mf.value
              try {
                var parsed = JSON.parse(mf.value)
                if (parsed && parsed.url) swatchUrl = parsed.url
              } catch (e) {}
              if (!colorSwatchMap[colorValue]) {
                colorSwatchMap[colorValue] = swatchUrl
              }
            }
            if (mf && mf.key === "color_code" && mf.value) {
              if (!colorCodeMap[colorValue]) {
                colorCodeMap[colorValue] = mf.value
              }
            }
          })
        }
      }
    })

    updateSwatchBackgrounds()
    injectVariantDataIntoTheme(allVariants)
    setupSwatchClickHandlers()
  }

  function injectVariantDataIntoTheme(apiVariants) {
    var productJson = document.querySelector("[data-product-json]")
    var productData = null

    if (productJson) {
      try {
        productData = JSON.parse(productJson.textContent)
      } catch (e) {}
    }

    var existingVariantIds = new Set()
    if (productData && productData.variants && Array.isArray(productData.variants)) {
      productData.variants.forEach((v) => {
        existingVariantIds.add(String(v.id))
      })
    }

    var newVariants = []
    apiVariants.forEach((variant) => {
      var numericId = variant.id.replace("gid://shopify/ProductVariant/", "")

      if (!existingVariantIds.has(numericId)) {
        var shopifyVariant = {
          id: Number.parseInt(numericId),
          sku: variant.sku || "",
          available: variant.availableForSale,
          title: variant.title,
          options: variant.selectedOptions.map((opt) => opt.value),
          option1: variant.selectedOptions[0] ? variant.selectedOptions[0].value : null,
          option2: variant.selectedOptions[1] ? variant.selectedOptions[1].value : null,
          option3: variant.selectedOptions[2] ? variant.selectedOptions[2].value : null,
          featured_image: variant.image
            ? {
                src: variant.image.url,
                alt: variant.image.altText || "",
              }
            : null,
          featured_media: variant.image
            ? {
                preview_image: {
                  src: variant.image.url,
                },
              }
            : null,
        }
        newVariants.push(shopifyVariant)
      }
    })

    if (productData && productJson) {
      if (!productData.variants) {
        productData.variants = []
      }
      productData.variants = productData.variants.concat(newVariants)
      productJson.textContent = JSON.stringify(productData)
    }

    if (window.productData) {
      if (!window.productData.variants) {
        window.productData.variants = []
      }
      window.productData.variants = window.productData.variants.concat(newVariants)
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      if (!window.ShopifyAnalytics.meta.product.variants) {
        window.ShopifyAnalytics.meta.product.variants = []
      }
      window.ShopifyAnalytics.meta.product.variants = window.ShopifyAnalytics.meta.product.variants.concat(newVariants)
    }

    window._v0AllVariants = apiVariants.map((v) => ({
      id: Number.parseInt(v.id.replace("gid://shopify/ProductVariant/", "")),
      sku: v.sku || "",
      available: v.availableForSale,
      title: v.title,
      option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
      option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
      option3: v.selectedOptions[2] ? v.selectedOptions[2].value : null,
      featured_image: v.image ? { src: v.image.url, alt: v.image.altText || "" } : null,
    }))

  }

  // Build reverse maps for translation matching
  var swatchUrlToColor = {}
  var colorCodeToColorName = {}
  
  function buildReverseMaps() {
    Object.keys(colorSwatchMap).forEach(function(color) {
      swatchUrlToColor[colorSwatchMap[color]] = color
    })
    Object.keys(colorCodeMap).forEach(function(color) {
      colorCodeToColorName[colorCodeMap[color]] = color
    })
  }
  
  function updateSwatchBackgrounds() {
    buildReverseMaps()
    
    var swatchLabels = document.querySelectorAll(".color-swatch__item[data-color-value]")

    swatchLabels.forEach((label) => {
      var translatedColorValue = label.getAttribute("data-color-value")
      var englishColorValue = translatedColorValue
      
      // Try direct match first
      var swatchUrl = colorSwatchMap[translatedColorValue]
      var colorCode = colorCodeMap[translatedColorValue]
      
      // If no direct match, try to find English color by existing background
      if (!swatchUrl && !colorCode) {
        var currentBgImage = label.style.backgroundImage || ""
        var currentBgColor = label.style.backgroundColor || ""
        
        // Extract URL from background-image
        var urlMatch = currentBgImage.match(/url\(["']?([^"')]+)["']?\)/)
        if (urlMatch && urlMatch[1]) {
          englishColorValue = swatchUrlToColor[urlMatch[1]]
          if (englishColorValue) {
            swatchUrl = colorSwatchMap[englishColorValue]
          }
        }
        
        // Try matching by color code
        if (!englishColorValue && currentBgColor) {
          englishColorValue = colorCodeToColorName[currentBgColor]
          if (englishColorValue) {
            colorCode = colorCodeMap[englishColorValue]
          }
        }
      }

      if (swatchUrl) {
        label.style.backgroundImage = "url('" + swatchUrl + "')"
        label.style.backgroundSize = "cover"
        label.style.backgroundPosition = "center"
        label.style.backgroundColor = ""
      } else if (colorCode) {
        label.style.backgroundColor = colorCode
        label.style.backgroundImage = ""
      }
      
      // Store English color for click handlers
      if (englishColorValue && englishColorValue !== translatedColorValue) {
        label.setAttribute("data-english-color", englishColorValue)
      }

      var swatchContainer = label.closest(".color-swatch")
      var variantData = variantDataMap[englishColorValue] || variantDataMap[translatedColorValue]
      if (swatchContainer && variantData && variantData.length > 0) {
        swatchContainer.classList.remove("color-swatch--disabled")
        var radioInput = swatchContainer.querySelector(".color-swatch__radio")
        if (radioInput) {
          radioInput.disabled = false
        }
      }
    })
  }

  function setupSwatchClickHandlers() {
    var allSwatches = document.querySelectorAll(".color-swatch")

    allSwatches.forEach((swatch) => {
      var label = swatch.querySelector(".color-swatch__item")
      var radio = swatch.querySelector(".color-swatch__radio")

      if (!label || !radio) return

      // Use English color if available (set during updateSwatchBackgrounds)
      var englishColor = label.getAttribute("data-english-color")
      var translatedColor = label.getAttribute("data-color-value") || radio.value
      var colorValue = englishColor || translatedColor

      function handleColorSelection() {
        // Try both English and translated color values
        var variants = variantDataMap[colorValue] || variantDataMap[translatedColor]

        updateGalleryForColor(colorValue)

        if (variants && variants.length > 0) {
          var variantWithImage =
            variants.find((v) => v.image && v.availableForSale) ||
            variants.find((v) => v.image) ||
            variants.find((v) => v.availableForSale) ||
            variants[0]

          if (variantWithImage) {
            var formInputs = document.querySelectorAll('input[name="id"], select[name="id"]')
            formInputs.forEach((input) => {
              input.value = variantWithImage.id
            })

            var url = new URL(window.location)
            url.searchParams.set("variant", variantWithImage.id)
            window.history.replaceState({}, "", url)

            var skuElement = document.querySelector(".product-meta__sku-number")
            var skuContainer = document.querySelector(".product-meta__sku")
            if (skuElement && variantWithImage.sku) {
              skuElement.textContent = variantWithImage.sku
              if (skuContainer) {
                skuContainer.style.display = ""
              }
            } else if (skuContainer && !variantWithImage.sku) {
              skuContainer.style.display = "none"
            }

            document.dispatchEvent(
              new CustomEvent("variant:change", {
                detail: {
                  variant: {
                    id: variantWithImage.id,
                    sku: variantWithImage.sku,
                    featured_image: variantWithImage.image ? { src: variantWithImage.image } : null,
                  },
                },
              }),
            )
          }
        }
      }

      label.addEventListener("click", (e) => {
        radio.checked = true
        radio.dispatchEvent(new Event("change", { bubbles: true }))
        handleColorSelection()
      })

      radio.addEventListener("change", (e) => {
        if (radio.checked) {
          handleColorSelection()
        }
      })
    })
  }

  function updateGalleryForColor(colorValue) {
    var normalizedColor = colorValue.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/\(/g, "").replace(/\)/g, "")

    var carouselItems = document.querySelectorAll(".product-gallery__carousel-item[data-group-value]")
    var thumbnailItems = document.querySelectorAll(".product-gallery__thumbnail[data-group-value]")

    var matchedCarouselItem = null
    var matchedThumbnail = null

    carouselItems.forEach((item) => {
      var groupValue = item.getAttribute("data-group-value")
      var normalizedGroupValue = groupValue ? groupValue.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/\(/g, "").replace(/\)/g, "") : ""

      if (
        normalizedGroupValue === normalizedColor ||
        groupValue === colorValue.toLowerCase() ||
        normalizedGroupValue.includes(normalizedColor) ||
        normalizedColor.includes(normalizedGroupValue)
      ) {
        matchedCarouselItem = item
      }
    })

    thumbnailItems.forEach((item) => {
      var groupValue = item.getAttribute("data-group-value")
      var normalizedGroupValue = groupValue ? groupValue.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/\(/g, "").replace(/\)/g, "") : ""

      if (
        normalizedGroupValue === normalizedColor ||
        groupValue === colorValue.toLowerCase() ||
        normalizedGroupValue.includes(normalizedColor) ||
        normalizedColor.includes(normalizedGroupValue)
      ) {
        matchedThumbnail = item
      }
    })

    if (matchedCarouselItem) {
      document.querySelectorAll(".product-gallery__carousel-item.is-selected").forEach((el) => {
        el.classList.remove("is-selected")
      })
      matchedCarouselItem.classList.add("is-selected")

      carouselItems.forEach((item) => {
        var groupValue = item.getAttribute("data-group-value")
        var normalizedGroupValue = groupValue ? groupValue.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/\(/g, "").replace(/\)/g, "") : ""

        if (
          normalizedGroupValue === normalizedColor ||
          groupValue === colorValue.toLowerCase() ||
          normalizedGroupValue.includes(normalizedColor) ||
          normalizedColor.includes(normalizedGroupValue)
        ) {
          item.classList.remove("is-filtered")
        } else if (groupValue) {
          item.classList.add("is-filtered")
        }
      })

      document.querySelectorAll(".product-gallery__carousel-item:not([data-group-value])").forEach((item) => {
        item.classList.remove("is-filtered")
      })

      var carousel = document.querySelector(".product-gallery__carousel")
      if (carousel && matchedCarouselItem) {
        var mediaId = matchedCarouselItem.getAttribute("data-media-id")
        if (mediaId) {
          var event = new CustomEvent("product:media-change", {
            bubbles: true,
            detail: { mediaId: mediaId },
          })
          carousel.dispatchEvent(event)
        }
      }
    }

    if (matchedThumbnail) {
      document.querySelectorAll(".product-gallery__thumbnail.is-nav-selected").forEach((el) => {
        el.classList.remove("is-nav-selected")
      })
      matchedThumbnail.classList.add("is-nav-selected")

      thumbnailItems.forEach((item) => {
        var groupValue = item.getAttribute("data-group-value")
        var normalizedGroupValue = groupValue ? groupValue.toLowerCase().replace(/\s+/g, "").replace(/-/g, "").replace(/\(/g, "").replace(/\)/g, "") : ""

        if (
          normalizedGroupValue === normalizedColor ||
          groupValue === colorValue.toLowerCase() ||
          normalizedGroupValue.includes(normalizedColor) ||
          normalizedColor.includes(normalizedGroupValue)
        ) {
          item.classList.remove("is-filtered")
        } else if (groupValue) {
          item.classList.add("is-filtered")
        }
      })

      document.querySelectorAll(".product-gallery__thumbnail:not([data-group-value])").forEach((item) => {
        item.classList.remove("is-filtered")
      })

      matchedThumbnail.click()
    }

    if (!matchedCarouselItem && !matchedThumbnail) {
      var variants = variantDataMap[colorValue]
      if (variants && variants.length > 0) {
        var variantWithImage = variants.find((v) => v.image)
        if (variantWithImage && variantWithImage.image) {
          updateProductImage(variantWithImage.image, variantWithImage.imageAlt)
        }
      }
    }
  }

  function updateProductImage(imageUrl, imageAlt) {
    if (!imageUrl) return

    var selectors = [
      ".product-gallery__carousel-item.is-selected img.product-gallery__image",
      ".product-gallery__carousel-item.is-selected img",
      ".product-gallery__image",
      ".product__media img",
      ".product__media-item--active img",
      ".product-gallery__image img",
      ".product-single__photo img",
      ".product__image",
      "[data-product-image]",
      ".product__media-wrapper img",
      ".product-featured-media img",
      "img.photoswipe__image",
      ".media-gallery__image",
      ".product__media-list .product__media:first-child img",
      ".product-images img:first-child",
      ".slick-current img",
      ".swiper-slide-active img",
    ]

    var imageUpdated = false

    for (var i = 0; i < selectors.length; i++) {
      var imgs = document.querySelectorAll(selectors[i])
      if (imgs.length > 0) {
        imgs.forEach((img) => {
          img.src = imageUrl
          img.srcset = ""
          if (imageAlt) img.alt = imageAlt

          if (img.hasAttribute("data-zoom")) {
            img.setAttribute("data-zoom", imageUrl)
          }
        })
        imageUpdated = true
        break
      }
    }

    if (!imageUpdated) {
      var carousel = document.querySelector(".product-gallery__carousel")
      if (carousel) {
        carousel.querySelectorAll(".product-gallery__carousel-item.is-selected").forEach((el) => {
          el.classList.remove("is-selected")
        })

        var targetItem = carousel.querySelector(".product-gallery__carousel-item:not([data-group-value])")
        if (!targetItem) {
          targetItem = carousel.querySelector(".product-gallery__carousel-item")
        }

        if (targetItem) {
          targetItem.classList.add("is-selected")
          targetItem.classList.remove("is-filtered")

          var img = targetItem.querySelector("img")
          if (img) {
            img.src = imageUrl
            img.srcset = ""
            if (imageAlt) img.alt = imageAlt
            if (img.hasAttribute("data-zoom")) {
              img.setAttribute("data-zoom", imageUrl)
            }
            imageUpdated = true
          }
        }
      }
    }

    if (!imageUpdated) {
      var productSection = document.querySelector('.product, .product-single, [data-section-type="product"]')
      if (productSection) {
        var mainImg = productSection.querySelector("img")
        if (mainImg) {
          mainImg.src = imageUrl
          mainImg.srcset = ""
          if (imageAlt) mainImg.alt = imageAlt
          return
        }
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fetchAllVariants)
  } else {
    fetchAllVariants()
  }
})()
